import { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext();

export const useSocket = () => {
    return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [activeUsersCount, setActiveUsersCount] = useState(0);
    const [activeUserIds, setActiveUserIds] = useState([]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const userRole = localStorage.getItem('userRole') || 'user';
        
        // Only connect for regular users
        if (token && userRole === 'user') {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
            // Convert to HTTPS for production, keep HTTP for localhost
            let socketUrl = apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;
            if (socketUrl.includes('localhost')) {
            // Keep HTTP for local development
            socketUrl = socketUrl;
            } else {
            // Convert to HTTPS for production
            socketUrl = socketUrl.replace('http://', 'https://');
            }
            
            const newSocket = io(socketUrl, {
                auth: {
                    token: token
                }
            });

            newSocket.on('connect', () => {
                console.log('Socket connected');
                setIsConnected(true);
            });

            newSocket.on('disconnect', () => {
                console.log('Socket disconnected');
                setIsConnected(false);
            });

            newSocket.on('connect_error', (error) => {
                console.error('Socket connection error:', error);
                setIsConnected(false);
            });

            // Listen for active users count updates (for admin clients)
            newSocket.on('active-users-count', (data) => {
                setActiveUsersCount(data.count);
                setActiveUserIds(data.userIds);
            });

            newSocket.on('user-connected', (data) => {
                console.log('User connected:', data);
            });

            newSocket.on('user-disconnected', (data) => {
                console.log('User disconnected:', data);
            });

            setSocket(newSocket);

            // Send heartbeat every 60 seconds
            const heartbeatInterval = setInterval(() => {
                if (newSocket.connected) {
                    newSocket.emit('heartbeat', {
                        userId: localStorage.getItem('userId'),
                        timestamp: new Date()
                    });
                }
            }, 60000);

            return () => {
                clearInterval(heartbeatInterval);
                newSocket.close();
            };
        }
    }, []);

    const disconnect = () => {
        if (socket) {
            socket.close();
            setSocket(null);
            setIsConnected(false);
        }
    };

    const value = {
        socket,
        isConnected,
        activeUsersCount,
        activeUserIds,
        disconnect
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};
